import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { ScrollPage } from '@/components/ScrollPage';
import { Button } from '@/components/ui/button';
import { Plus, Search, Edit, Trash2, Loader2, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useCheckEmailAvailability,
} from '@/hooks/useUsers';
import { useRoles } from '@/hooks/useRoles';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import { useAuth } from '@/contexts/AuthContext';
import { User, CreateUserPayload, UpdateUserPayload } from '@/types';
import { debounce } from '@/lib/debounce';

export default function Users() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.roles?.includes('Admin');

  const { data: users, isLoading: usersLoading } = useUsers();
  const { data: roles, isLoading: rolesLoading } = useRoles();
  const { data: businessUnits, isLoading: businessUnitsLoading } = useBusinessUnits();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    userName: '',
    role: '',
    businessUnitId: undefined as number | undefined,
  });

  // Email availability state
  const [emailToCheck, setEmailToCheck] = useState('');
  const [shouldCheckEmail, setShouldCheckEmail] = useState(false);
  const { data: emailAvailability } = useCheckEmailAvailability(emailToCheck, shouldCheckEmail);

  // Debounced email check - create the debounced function once
  const debouncedEmailCheckFn = useMemo(
    () => debounce((email: string) => {
      if (email && email.includes('@') && !editingUser) {
        setEmailToCheck(email);
        setShouldCheckEmail(true);
      } else {
        setShouldCheckEmail(false);
      }
    }, 500),
    [editingUser]
  );

  useEffect(() => {
    debouncedEmailCheckFn(formData.email);
  }, [formData.email, debouncedEmailCheckFn]);

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        userName: user.userName,
        role: user.roles?.[0] || '',
        businessUnitId: user.businessUnitId,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        userName: '',
        role: '',
        businessUnitId: undefined,
      });
    }
    setShouldCheckEmail(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingUser(null);
    setFormData({
      email: '',
      userName: '',
      role: '',
      businessUnitId: undefined,
    });
    setShouldCheckEmail(false);
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.role || !formData.businessUnitId) {
      return;
    }

    if (editingUser) {
      // Update existing user
      const payload: UpdateUserPayload = {
        id: editingUser.id,
        email: formData.email,
        userName: formData.userName || formData.email,
        role: formData.role,
        businessUnitId: formData.businessUnitId,
      };
      await updateUserMutation.mutateAsync({ id: editingUser.id, payload });
    } else {
      // Create new user
      const payload: CreateUserPayload = {
        email: formData.email,
        role: formData.role,
        businessUnitId: formData.businessUnitId,
      };
      await createUserMutation.mutateAsync(payload);
    }
    handleCloseDialog();
  };

  const handleOpenDeleteDialog = (user: User) => {
    setDeletingUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingUser(null);
  };

  const handleDelete = async () => {
    if (deletingUser) {
      await deleteUserMutation.mutateAsync(deletingUser.id);
      handleCloseDeleteDialog();
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'default';
      case 'bu_manager':
      case 'manager':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Filter users by search query
  const filteredUsers =
    users?.filter(
      (user) =>
        user.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.roles?.some((role) => role.toLowerCase().includes(searchQuery.toLowerCase()))
    ) || [];

  const isFormValid = formData.email && formData.role && formData.businessUnitId && (!shouldCheckEmail || emailAvailability?.isAvailable);

  return (
    <Layout>
      <ScrollPage
        framed={!usersLoading && filteredUsers.length > 0}
        footer={
          !usersLoading && filteredUsers.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}
            </p>
          ) : null
        }
        header={
          <>
        <PageHeader
          title="Users"
          description="Manage user access and permissions"
          actions={
            isAdmin && (
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4" />
                New User
              </Button>
            )
          }
        />

        <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold">All Users ({filteredUsers.length})</h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          </>
        }
      >
          {usersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Business Unit</TableHead>
                    <TableHead>Roles</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-muted-foreground">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.userName || 'N/A'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.businessUnitName || <span className="text-muted-foreground">Not assigned</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {user.roles?.map((role, idx) => (
                              <Badge key={idx} variant={getRoleBadgeVariant(role)}>
                                {role}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleOpenDialog(user)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleOpenDeleteDialog(user)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
      </ScrollPage>

        {/* User Dialog (Create/Edit) */}
        <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
              <DialogDescription>
                {editingUser
                  ? 'Update user information and role assignment.'
                  : 'Create a new user account with email and role.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={editingUser !== null}
                />
                {shouldCheckEmail && emailAvailability && (
                  <div className="flex items-center gap-2 text-sm">
                    {emailAvailability.isAvailable ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-success-foreground" />
                        <span className="text-success-foreground">Email available</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-destructive" />
                        <span className="text-destructive">Email already exists</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              {editingUser && (
                <div className="grid gap-2">
                  <Label htmlFor="userName">User Name</Label>
                  <Input
                    id="userName"
                    placeholder="John Doe"
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                  />
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {rolesLoading ? (
                      <div className="p-2 text-center text-sm text-muted-foreground">
                        Loading roles...
                      </div>
                    ) : (
                      roles?.map((role) => (
                        <SelectItem key={role.id} value={role.name}>
                          {role.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="businessUnit">Business Unit *</Label>
                <Select
                  value={formData.businessUnitId?.toString() || ""}
                  onValueChange={(value) => setFormData({ ...formData, businessUnitId: value ? parseInt(value) : undefined })}
                >
                  <SelectTrigger id="businessUnit">
                    <SelectValue placeholder="Select a business unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnitsLoading ? (
                      <div className="p-2 text-center text-sm text-muted-foreground">
                        Loading business units...
                      </div>
                    ) : (
                      businessUnits?.map((bu) => (
                        <SelectItem key={bu.id} value={bu.id.toString()}>
                          {bu.name} ({bu.code})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !isFormValid ||
                  createUserMutation.isPending ||
                  updateUserMutation.isPending
                }
              >
                {(createUserMutation.isPending || updateUserMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingUser ? 'Save Changes' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={handleCloseDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete user <strong>{deletingUser?.email}</strong>? This
                action will soft-delete the user and can be reversed by an administrator.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteUserMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteUserMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </Layout>
  );
}
